import React from "react";
import { NavItem } from "reactstrap";

//Common
import { DataGrid, ConfirmationDialog, Snackbar, Drawer, Chip, Button, } from "../common/VFX-UI";
import { MoreActionButton } from "../common/Buttons";
import PageHeader from "../common/PageHeader/PageHeader";
import MoreActionsMenu from "../common/MoreActionsMenu/MoreActionsMenu";
import SearchContainer from "../common/Forms/SearchContainer/SearchContainer";
import { SearchFieldsDefaultValue } from "../../utility/constants/constants";
import { ResendIcon, ViewIcon } from "../common/Icons";
import useMoreActionsMenuStyles from "../common/MoreActionsMenu/styles";

//App
import { hasPermission, permissions } from "../../utility/helpers/claimHelper";
import { OrganizationCampaignDto } from "../../app/model/api/types/dto/organizationCampaignDto";
import CampaignView from "./CampaignView";
import { CampaignFormContainer } from "./CampaignFormContainer";
import useCampaignStyles from "./styles";
import useCampaign from "./hooks/useCampaign";
import { CampaignProps } from "../../types/campaign";
import { SearchProps } from "../../types";
import { SearchDefaultValue } from "../../store/types";
import { getFormattedDate } from "../../utility/lib/date";
import { ColumnDescription } from "react-bootstrap-table-next";
import { useNavigate } from "react-router-dom";

const Campaigns: React.FC<CampaignProps> = (props) => {
    const navigate = useNavigate();
    const campaignStyles = useCampaignStyles();
    const moreActionMenuStyles = useMoreActionsMenuStyles();

    const {
        data,
        activateMutation,
        deleteMutation,
        sendEmployeeMail,
        fullscreen,
    } = props;

    const {
        state,
        formContext,
        rowEvents,
        actionColumnAnchorEl,
        toggleNewViewHandler,
        toggleViewHandler,
        toggleEditViewHandler,
        toggleActivateHandler,
        toggleDeactivateHandler,
        handleDeactivate,
        handleActivate,
        onShowSuccess,
        onAddSnackbarClose,
        handleActionClick,
        setActionColumnAnchorEl,
        handleActiveToggleChange,
    } = useCampaign({
        deleteMutation: deleteMutation,
        activateMutation: activateMutation,
        sendEmployeeMail: sendEmployeeMail,
    });

    const openMenu = Boolean(actionColumnAnchorEl);

    var columns: ColumnDescription[] = [
        {
            dataField: "id",
            text: "ID",
            hidden: true,
        },
    ];

    if (fullscreen === null || fullscreen === undefined || fullscreen) {
        columns = [
            ...columns,
            {
                dataField: "code",
                text: "Code",
                sort: false,
            },
            {
                dataField: "name",
                text: "Name",
                sort: true,
            },
            {
                dataField: "startDate",
                text: "Campaign Duration",
                sort: true,
                formatter: (cell: Date, row: any) => {
                    return getFormattedDate(cell.toString()) + ' - ' + getFormattedDate(row.endDate.toString());
                }
            },
        ]
    }
    else {
        columns = [
            ...columns,
            {
                dataField: "name",
                text: "Name",
                sort: true,
            },
            {
                dataField: "startDate",
                text: "Campaign Duration",
                sort: true,
                formatter: (cell: Date, row: any) => {
                    return getFormattedDate(cell.toString()) + ' - ' + getFormattedDate(row.endDate.toString());
                }
            },
        ]
    }

    columns = [
        ...columns,
        {
            dataField: "isActive",
            text: "Status",
            sort: false,
            formatter: (cell: boolean, row: any) => {
                if (row.isCompleted) return <Chip label={"Completed"} />;
                else return <Chip label={!row.isActive ? "Inactive" : row.status} />;
            },
        },
        {
            dataField: "action",
            text: "",
            style: { width: "25px" },
            isDummyField: true,
            formatter: () => {
                return (
                    <MoreActionButton handleActionClick={(e) => handleActionClick(e)} />
                );
            },
        },
    ]

    const searchProps: SearchProps<OrganizationCampaignDto> = {
        searchValue: formContext?.searchValue ?? SearchDefaultValue,
        searchFields: { ...SearchFieldsDefaultValue, name: true },
        setSearchValue: formContext?.setSearchValue,
        refetchGrid: formContext?.refetchGrid,
    };
    return (
        <>
            <>
                <PageHeader>
                    {"Campaigns"}
                </PageHeader>

                <div style={campaignStyles.searchCampaignContainer}>
                    <SearchContainer
                        searchProps={searchProps}
                        activeToggle={formContext?.activeToggle ?? true}
                        handleActiveToggleChange={handleActiveToggleChange}
                        toggleNewViewHandler={
                            hasPermission([permissions.MANAGE_CAMPAIGNS]) ? toggleNewViewHandler : undefined
                        }
                    />
                    {/*<div className="mb-3" style={campaignStyles.completedButtonContainer}>*/}
                    {/*    <Button*/}
                    {/*        onClick={updateCompleteToggle}*/}
                    {/*        style={campaignStyles.completeButton}*/}
                    {/*    >*/}
                    {/*        {!isCompletedCampaigns ? "Completed Campaigns" : "Back"}*/}
                    {/*    </Button>*/}
                    {/*</div>*/}
                </div>
            </>

            <Drawer show={state.toggleNewForm}>
                {state.toggleNewForm && (
                    <CampaignFormContainer
                        onClose={toggleNewViewHandler}
                        onShowSuccess={onShowSuccess}
                    />
                )}
            </Drawer>

            <Drawer show={state.toggleEditForm}>
                {state.toggleEditForm && formContext?.data && (
                    <CampaignFormContainer
                        onClose={toggleEditViewHandler}
                        onShowSuccess={onShowSuccess}
                    />
                )}
            </Drawer>

            <Drawer show={state.toggleView}>
                {state.toggleView && formContext?.data && (
                    <CampaignView
                        onClose={toggleViewHandler}
                        editAction={() => {
                            toggleEditViewHandler();
                        }}
                        campaignData={formContext?.data}
                        toggleDeactivateHandler={toggleDeactivateHandler}
                        toggleActivateHandler={toggleActivateHandler}
                    />
                )}
            </Drawer>

            <DataGrid
                columns={columns}
                data={data}
                keyField="id"
                rowEvents={rowEvents}
                totalSize={formContext?.paging?.totalSize}
                remote={true}
                page={formContext?.paging?.currentPage}
                pageSize={formContext?.pageSize}
                handlePageChange={async (newPage, pageSize, sortField, sortOrder) => {
                    if (formContext?.setCurrentPage && newPage) {
                        await formContext?.setCurrentPage(newPage);
                    }
                    if (formContext?.setCurrentPageSize && pageSize) {
                        await formContext?.setCurrentPageSize(pageSize);
                    }
                    if (formContext?.setSortBy && sortField && sortOrder) {
                        await formContext?.setSortBy({
                            field: sortField,
                            direction: sortOrder === "asc" ? 1 : -1,
                        });
                    }
                    formContext?.refetchGrid();
                }}
            />

            {actionColumnAnchorEl && (
                <MoreActionsMenu
                    isOpen={openMenu}
                    target={actionColumnAnchorEl}
                    toggle={() => {
                        setActionColumnAnchorEl(null);
                    }}
                    onViewClick={() => {
                        toggleViewHandler();
                        setActionColumnAnchorEl(null);
                    }}
                >

                    {formContext?.data?.status?.toLowerCase() !== "inactive" && (
                        <NavItem
                            style={moreActionMenuStyles.navItem}
                            onClick={() => {
                                if (formContext?.id) {
                                    setActionColumnAnchorEl(null);
                                    navigate("/donors/" + formContext.id)
                                }
                            }}
                        >
                            <ViewIcon iconText="View Donors" />
                        </NavItem>
                    )}

                    {hasPermission([permissions.MANAGE_CAMPAIGNS]) && sendEmployeeMail &&
                        formContext?.data?.status?.toLowerCase() === "active" && (
                            <NavItem
                                style={moreActionMenuStyles.navItem}
                                onClick={async () => {
                                    if (formContext?.id) {
                                        const response = await sendEmployeeMail();
                                        setActionColumnAnchorEl(null);
                                        if (response) {
                                            onShowSuccess("Email Sent", false);
                                        }
                                    }
                                }}
                            >
                                <ResendIcon iconText="Send Campaign Reminders" />
                            </NavItem>
                        )}
                </MoreActionsMenu>
            )}

            <ConfirmationDialog
                open={state.isDeactivateDialogOpen}
                onClose={toggleDeactivateHandler}
                onConfirm={handleDeactivate}
                contentTitle={`Deactivate ${formContext?.data?.name}`}
                contentText={"Are you sure you want to continue?"}
                confirmText={"Yes, Continue"}
                closeText={"No, Go Back"}
            ></ConfirmationDialog>

            <ConfirmationDialog
                open={state.isActivateDialogOpen}
                onClose={toggleActivateHandler}
                onConfirm={handleActivate}
                contentTitle={`Activate ${formContext?.data?.name}`}
                contentText={"Are you sure you want to continue?"}
                confirmText={"Yes, Continue"}
                closeText={"No, Go Back"}
            />

            {state.showSuccessSnackbar && (
                <Snackbar
                    open={state.showSuccessSnackbar}
                    message={state.successMessage}
                    handleClose={onAddSnackbarClose}
                    severity="success"
                />
            )}
        </>
    );
};

export default Campaigns;
